import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { downloadUrlFromRemoteOrigin } from "@/lib/utility/downloadFileFunc";
import { GoToHelper } from "@/lib/utility/goToHelper";
import { handleError } from "@/lib/utility/handleError";
import { DocumentTokenCoordinates, IDocumentTokenCoordinates } from "@/models/document-token-coordinates.entity";
import { DocumentTokenDto, IDocumentTokenDto } from "@/models/document-token.entity";
import documentService from "@/services/sign/services/document.service";
import { SignDocumentViewModelGen, SignUpdateDocumentDtoGen } from "@/services/sign/v1/data-contracts";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { SignDocumentDataAccessLayer } from "@/store/modules/access-layers/sign-document.access-layer";
import { TagDataAccessLayer } from "@/store/modules/access-layers/tag.access-layer";
import VueRouter from "vue-router";
import { ActivityLog } from "./activity-log.entity";
import { IReference, Reference } from "./reference.entity";
import { ITag, Tag } from "./tag.entity";
import { ITimestampDocument, TimestampDocument } from "./timestamp.entity";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { $t } from "@/lib/utility/t";
import { PartnerUserAccessLayer } from "@/store/modules/access-layers/partner-user.access-layer";

export { DocumentTokenCoordinates, DocumentTokenDto, IDocumentTokenCoordinates, IDocumentTokenDto };

interface IDisplayHint {
  icon: string;
  tooltip: string;
}

@IsFilterable
class SignDocumentBase implements IEntity<SignDocumentViewModelGen, SignUpdateDocumentDtoGen> {
  /** The partner id of the document */
  partnerId: string;

  /** The id of the user that created the document */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.document.userId",
    config: {
      itemCallback: () => PartnerUserAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      itemText: "fullName",
      component: "refs-user",
      refType: BackendResourceEnum.USER
    }
  })
  userId: string;

  /** The id of the document */
  id: string;

  @FilterConfig({ type: FilterTypes.OBJECT_ID, displayName: "objects.document.id" })
  get _id() {
    return this.id;
  }

  /** The name of the document */
  @FilterConfig({ type: FilterTypes.STRING, displayName: "objects.document.name" })
  name: string;

  /** The folder of the document */
  @FilterConfig({ type: FilterTypes.STRING, displayName: "objects.document.folder" })
  folder: string;

  /** The url of the document */
  url: string;

  /** The type of the document */
  @FilterConfig({ type: FilterTypes.STRING, displayName: "objects.document.type" })
  type: string;

  /** The title of the document */
  @FilterConfig({ type: FilterTypes.STRING, displayName: "objects.document.title" })
  title: string;

  /** The text of the document */
  text: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.company.tagIds",
    config: {
      itemCallback: () => TagDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ value: item, size: "small" }),
      itemValue: "id",
      component: "tag-chip"
    }
  })
  tagIds: string[] = [];

  tags: ITag[] = [];

  /** Indicates whether the document has been linked to an entity */
  isUsed: boolean;

  /** Is the document a template */
  @FilterConfig({ type: FilterTypes.BOOLEAN, displayName: "objects.document.isTemplate" })
  isTemplate: boolean;

  /** Is the document signed */
  @FilterConfig({ type: FilterTypes.BOOLEAN, displayName: "objects.document.isSigned" })
  isSigned: boolean;

  /** Is the document shared */
  isShared: boolean;

  /** Is the document deletable */
  isDeletable: boolean;

  /** Is the document internal - getter without filter decorator */
  @FilterConfig({ type: FilterTypes.BOOLEAN, displayName: "objects.document.isInternal" })
  get isInternal(): boolean {
    return !this.isShared;
  }

  /** Is the document internal - setter */
  set isInternal(value: boolean) {
    this.isShared = !value;
  }

  /** Loading state for internal toggle */
  isLoadingInternal = false;

  /**
   * Indicates if there was an error during upload
   */
  isUploadError = false;

  /**
   * The timestamps of the document
   * @example {"created":"2021-01-01T12:12:12.317Z","lastModified":"2021-01-01T12:12:12.317Z","modified":["2021-01-01T12:12:12.317Z","2021-01-01T12:12:12.000Z"]}
   */
  @FilterConfig({ type: TimestampDocument })
  timestamp: ITimestampDocument;

  /** The description of the document */
  @FilterConfig({ type: FilterTypes.STRING, displayName: "objects.document.description" })
  description?: string;

  /** The refs of the document */
  @FilterConfig({ type: Reference })
  refs: IReference[];

  /**
   * The size of the document in bytes
   */
  sizeInBytes?: number;

  /** The source the document was provided from */
  contactType: SignDocumentViewModelGen["contactType"];

  /** The tokens of the document */
  tokens: IDocumentTokenDto[][];

  file?: File;

  constructor(document?: Partial<SignDocumentViewModelGen & { file: File }>) {
    this.partnerId = document?.partnerId ?? "";
    this.userId = document?.userId ?? "";
    this.id = document?.id ?? "";
    this.name = document?.name ?? "";
    this.folder = document?.folder ?? "";
    this.url = document?.url ?? "";
    this.type = document?.type ?? "";
    this.title = document?.title || document?.name || "";
    this.isUsed = document?.isUsed ?? false;
    this.isTemplate = document?.isTemplate ?? false;
    this.isSigned = document?.isSigned ?? false;
    this.isShared = document?.isShared ?? false;
    this.isDeletable = document?.isDeletable ?? false;
    this.timestamp = new TimestampDocument(document?.timestamp);
    this.description = document?.description;
    this.refs = Reference.filterDuplicates((document?.refs ?? []).map((ref) => new Reference(ref)));
    this.tokens = document?.tokens?.map((token) => token.map((t) => new DocumentTokenDto(t))) ?? [];
    this.file = document?.file;
    this.text = document?.text ?? "";
    this.tagIds = document?.tagIds ?? [];
    this.tags = (document?.tags ?? []).map((tag) => new Tag(tag));
    this.sizeInBytes = (document as any)?.pdf?.metaData?.size;
    this.contactType = document?.contactType ?? "other";
  }

  map(document?: SignDocumentViewModelGen) {
    if (!document) return;

    this.partnerId = document.partnerId;
    this.userId = document.userId ?? "";
    this.id = document.id;
    this.name = document.name;
    this.folder = document.folder;
    this.url = document.url;
    this.type = document.type;
    this.title = document?.title || document?.name || "";
    this.isUsed = document?.isUsed ?? false;
    this.isTemplate = document.isTemplate;
    this.isSigned = document.isSigned;
    this.isShared = document.isShared;
    this.isDeletable = document?.isDeletable ?? false;
    this.timestamp = new TimestampDocument(document?.timestamp);
    this.description = document.description;
    this.refs = Reference.filterDuplicates((document?.refs ?? []).map((ref) => new Reference(ref)));
    this.tokens = document.tokens?.map((token) => token.map((t) => new DocumentTokenDto(t))) ?? [];
    this.text = document.text ?? "";
    this.tagIds = document.tagIds ?? [];
    this.tags = (document.tags ?? []).map((tag) => new Tag(tag));
    this.sizeInBytes = (document as any)?.pdf?.metaData?.size;
    this.contactType = document.contactType ?? "other";
  }

  /**
   * return the size of the file or the stored size after upload
   */
  get size() {
    return this.file?.size || this.sizeInBytes || 0;
  }

  /**
   * Returns a combined icon and tooltip based on document state and contactType.
   * Priority: isTemplate > isSigned > isShared > contactType
   */
  get displayHint(): IDisplayHint {
    if (this.isTemplate) return { icon: "mdi-file-hidden", tooltip: $t("sign.DocumentDetail.template") };
    if (this.isSigned) return { icon: "mdi-signature-freehand", tooltip: $t("sign.DocumentDetail.signed") };
    if (!this.isInternal) return { icon: "mdi-file-move-outline", tooltip: $t("sign.DocumentTable.shared") };
    if (this.contactType === "report")
      return { icon: "mdi-file-upload-outline", tooltip: $t("sign.DocumentTable.fromOutside") };
    return { icon: "mdi-file-outline", tooltip: $t("sign.DocumentDetail.file") };
  }

  async updateIsInternal(isInternal: boolean): Promise<void> {
    try {
      this.isLoadingInternal = true;
      const res = await this.updatePartial({
        isShared: !isInternal
      });

      await new ActivityLog({
        partnerId: this.partnerId,
        source: {
          refType: BackendResourceEnum.DOCUMENT,
          refId: this.id
        },
        comment: `${isInternal}`,
        actionType: ActionEnum.UPDATE,
        activity: ActivityTypeEnum.UPDATE_DOCUMENT_SHARED
      }).create();

      this.map(res);
    } finally {
      this.isLoadingInternal = false;
    }
  }

  async fetch(): Promise<this> {
    const res = await documentService.getOneForPartner(this.partnerId, this.id);

    this.map(res);

    SignDocumentDataAccessLayer.set(this);

    return this;
  }

  async update() {
    try {
      const res = await documentService.updateForPartner(this.partnerId, this.id, {
        file: this.file,
        description: this.description,
        title: this.title,
        refs: Reference.filterDuplicates((this.refs ?? []) as IReference[]),
        folder: this.folder,
        isTemplate: this.isTemplate,
        isShared: this.isShared,
        name: this.name,
        tokens: this.tokens,
        tagIds: this.tags.map((tag) => tag.id) || []
      });

      this.map(res);

      SignDocumentDataAccessLayer.set(this);
    } catch (e) {
      handleError(e);
    }

    return this;
  }

  async updatePartial(dto: SignUpdateDocumentDtoGen) {
    const res = await documentService.updateForPartner(this.partnerId, this.id, dto);

    this.map(res);

    SignDocumentDataAccessLayer.set(this);

    return this;
  }

  async updateTokens() {
    try {
      const res = await documentService.updateDocumentTokens(this.partnerId, this.id, {
        tokens: this.tokens
      });

      this.map(res);

      SignDocumentDataAccessLayer.set(this);
    } catch (e) {
      handleError(e);
    }

    return this;
  }

  async delete(): Promise<void> {
    try {
      const res = await documentService.removeForPartner(this.partnerId, this.id);

      this.map(res);

      SignDocumentDataAccessLayer.delete(this);
    } catch (e) {
      handleError(e);
    }
  }

  async create(): Promise<this> {
    if (!this.file) {
      throw new Error("File is required");
    }
    this.isUploadError = false;
    this.isLoadingInternal = true;

    try {
      const res = await documentService.create(this.partnerId, {
        file: this.file,
        description: this.description ?? "",
        name: this.name,
        title: this.title,
        folder: this.folder,
        isTemplate: this.isTemplate,
        isShared: this.isShared,
        refs: Reference.filterDuplicates((this.refs ?? []) as IReference[]),
        tokens: this.tokens,
        tagIds: this.tags.map((tag) => tag.id) || []
      });
      this.map(res);

      SignDocumentDataAccessLayer.set(this);

      if (this.tagIds?.length) {
        await new ActivityLog().createTagActivity({
          source: {
            refType: BackendResourceEnum.DOCUMENT,
            refId: this.id
          },
          partnerId: this.partnerId,
          tagIds: this.tagIds,
          activityType: ActivityTypeEnum.CREATE_TAG
        });
      }
    } catch (error) {
      this.isUploadError = true;

      throw error;
    } finally {
      this.isLoadingInternal = false;
    }

    return this;
  }

  static normalizePdfFileName(fileName: string): string {
    return fileName
      .replace(/\.pdf$/i, "")
      .replace(/[^a-zA-Z0-9À-ž]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  async download(): Promise<boolean> {
    return await downloadUrlFromRemoteOrigin(this.url, this.title || this.name);
  }

  async goToDetail(router: VueRouter, newTab = false) {
    new GoToHelper(router).goToDocumentDetail(this.id, this.partnerId, newTab);
  }

  goTo(router: VueRouter) {
    return {
      table: () => new GoToHelper(router).goToDocumentTableView(this.partnerId),
      detail: () => this.goToDetail(router)
    };
  }
}

type ISignDocument = SignDocumentBase;
const SignDocument = Filter.createForClass(SignDocumentBase);

export { ISignDocument, SignDocument };
